import React, { useState, useEffect, useCallback } from "react";
import axios from '../../api/apiClient';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import './../../App.css';
import './../Transaction/Transactions.css';

import {
  CreateTableNew as CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../common/APICommon';
import { SetAuditTrail, SetAuditTrailBulk } from './../Reports/AuditTrailReportEmployeeSetup';
import {
  Input,
} from '../../common/Input';
import { token, domain } from '../Security';
import { LoadingSpinner } from '../../common/Loading';
// icon
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';

function Requirements({ user, isHideSideMenu }) {
  // Use the hook for message management
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  // --- TAB STATE ---
  const [activeTab, setActiveTab] = useState(0); // 0 = Categories, 1 = Requirements

  // --- API URLS ---
  const apiUrlRequirement = domain + "/api/OnBoardingRequirements";
  // NOTE: Adjust this endpoint to match your actual backend controller for categories
  const apiUrlCategory = domain + "/api/RequirementCategory";

  // --- TABLE HEADERS ---
  // Tab 0: Categories
  const headersCategory = ["ID", "Category", "Status", "Actions"];
  const accessorsCategory = ["id", "description", "isActive", "actions"];

  // Tab 1: Requirements
  const headersRequirement = ["ID", "Category", "Description", "Status", "Actions"];
  const accessorsRequirement = ["id", "categoryName", "description", "isActive", "actions"];

  // --- STATE ---
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);

  // Data Storage
  const [categories, setCategories] = useState([]); // List of categories
  const [requirements, setRequirements] = useState([]); // List of requirements
  const [displayData, setDisplayData] = useState([]); // Data currently shown in table
  const [filterData, setFilterData] = useState([]); // Master copy for filtering

  const [selectedData, setSelectedData] = useState(null);

  // Form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  const [form, setForm] = useState({
    id: "",
    categoryId: "", // For Requirements
    description: "", // Used for both Category Name and Requirement Description
    isActive: true,
  });

  const resetLocalVariables = () => {
    setForm({
      id: "",
      categoryId: "",
      description: "",
      isActive: true,
    });
    setSelectedData(null);
  }

  // --- FETCH DATA ---

  // Fetch Categories (Used for Tab 0 AND for the Dropdown in Tab 1)
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(apiUrlCategory, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCategories(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  }, [apiUrlCategory]);

  // Fetch Requirements
  const fetchRequirements = useCallback(async () => {
    try {
      const response = await axios.get(apiUrlRequirement, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setRequirements(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching requirements:', error);
      return [];
    }
  }, [apiUrlRequirement]);

  // Master Fetch
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Always fetch categories (we need them for mapping names in requirements)
      const cats = await fetchCategories();

      if (activeTab === 0) {
        setDisplayData(cats);
        setFilterData(cats);
      } else {
        const reqs = await fetchRequirements();
        // Map category ID to Name for display
        const mappedReqs = reqs.map(r => {
          const cat = cats.find(c => c.id === r.requirementCategoryId);
          return {
            ...r,
            categoryName: cat ? cat.description : "-", // specific for table display
            categoryId: r.requirementCategoryId // ensure consistency for edit
          };
        });
        setDisplayData(mappedReqs);
        setFilterData(mappedReqs);
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab, fetchCategories, fetchRequirements]);

  useEffect(() => {
    fetchData();
    setCurrentPage(1); // Reset page on tab change
  }, [fetchData]);


  // --- HANDLERS ---

  const handleTabSelect = (index) => {
    setActiveTab(index);
    // Data fetching is handled by the useEffect dependent on activeTab
  };

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();

    if (!value) {
      setDisplayData(filterData);
      return;
    }

    const res = filterData.filter((f) => {
      // Common filters
      const matchId = f.id?.toString().startsWith(lowercaseValue);
      const matchDesc = f.description?.toString().toLowerCase().includes(lowercaseValue);

      // Requirement specific filter (Category Name)
      let matchCat = false;
      if (activeTab === 1 && f.categoryName) {
        matchCat = f.categoryName.toLowerCase().includes(lowercaseValue);
      }

      return matchId || matchDesc || matchCat;
    });

    setDisplayData(res);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    let apiData = {};
    let url = "";
    let auditField = "";

    if (activeTab === 0) {
      // Create Category
      url = apiUrlCategory;
      apiData = {
        description: form.description,
        isActive: form.isActive
      };
      auditField = "Add Category";
    } else {
      // Create Requirement
      url = apiUrlRequirement;
      apiData = {
        requirementCategoryId: form.categoryId,
        description: form.description,
        isActive: form.isActive
      };
      auditField = "Add Requirement";
    }

    try {
      setLoadingRequest(true);
      await axios.post(url, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      await SetAuditTrail({
        setupArea: "Requirements",
        actionTaken: "insert",
        field: auditField,
        newValue: `${apiData.description}`,
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id
      });

      await fetchData();
      showGeneralMessage("Record created successfully!", true);
      closeAddHandleClick();
    } catch (error) {
      console.error('Error creating data:', error);
      const errorMessage = error.response?.data?.message || "Failed to create record";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();

    if (!selectedData?.id) return;

    let apiData = {};
    let url = "";
    let auditBase = "";
    let oldDesc = selectedData.description;
    let auditInfo = [];

    if (activeTab === 0) {
      // Update Category
      url = `${apiUrlCategory}/${selectedData.id}`;
      apiData = {
        id: selectedData.id,
        description: form.description,
        isActive: form.isActive
      };
      auditBase = "Category";
    } else {
      // Update Requirement
      url = `${apiUrlRequirement}/${selectedData.id}`;
      apiData = {
        id: selectedData.id,
        requirementCategoryId: form.categoryId,
        description: form.description,
        isActive: form.isActive
      };
      auditBase = "Requirement";

      // Audit for Category Change
      if (apiData.requirementCategoryId !== selectedData.requirementCategoryId) {
        // Find names for audit
        const newCatName = categories.find(c => c.id == apiData.requirementCategoryId)?.description;
        const oldCatName = categories.find(c => c.id == selectedData.requirementCategoryId)?.description;

        auditInfo.push({
          setupArea: "Requirements",
          actionTaken: "update",
          field: "Category",
          newValue: `${newCatName}`,
          oldValue: `${oldCatName}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }
    }

    try {
      setLoadingRequest(true);
      await axios.put(url, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Standard Audits
      if (apiData.description !== oldDesc) {
        auditInfo.push({
          setupArea: "Requirements",
          actionTaken: "update",
          field: `${auditBase} Description`,
          newValue: `${apiData.description}`,
          oldValue: `${oldDesc}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (apiData.isActive !== selectedData.isActive) {
        auditInfo.push({
          setupArea: "Requirements",
          actionTaken: "update",
          field: `${auditBase} Is Active?`,
          newValue: `${apiData.isActive ? "Active" : "Inactive"}`,
          oldValue: `${selectedData.isActive ? "Active" : "Inactive"}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (auditInfo.length > 0) {
        await SetAuditTrailBulk({ data: auditInfo });
      }

      setShowEditForm(false);
      await fetchData();
      showGeneralMessage("Record updated successfully!", true);
      closeEditHandleClick();
    } catch (error) {
      console.error('Error updating data:', error);
      const errorMessage = error.response?.data?.message || "Failed to update record";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const editHandleClick = (data) => {
    setSelectedData(data);
    setForm({
      id: data.id,
      description: data.description || "",
      categoryId: data.requirementCategoryId || "", // Maps to API FK
      isActive: data.isActive !== undefined ? data.isActive : true,
    });
    setShowEditForm(true);
  };

  const openAddHandleClick = () => {
    setShowAddForm(true);
  };

  const closeAddHandleClick = () => {
    setShowAddForm(false);
    resetLocalVariables();
  };

  const closeEditHandleClick = () => {
    setShowEditForm(false);
    resetLocalVariables();
  };

  const actions = [
    {
      name: "Edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: editHandleClick,
      visible: true,
    },
  ];

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="MainteInfo shadow-xl">
            <div className="mainteHeader">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Maintenance >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"Requirements"}</span>
              </div>
              <h3>Requirements</h3>
            </div>

            <div className='loan-tab p-4 space-y-4'>
              <Tabs selectedIndex={activeTab} onSelect={handleTabSelect}>
                <TabList>
                  <Tab><b>CATEGORIES</b></Tab>
                  <Tab><b>REQUIREMENTS</b></Tab>
                </TabList>

                {/* Common Controls for both tabs */}
                <div className="mt-3 mb-5">
                  <SearchBar handleFilter={handleFilter} />
                  <div className="my-4">
                    <Button icon={<AddIcon />}
                      name={activeTab === 0 ? "Add Category" : "Add Requirement"}
                      className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
                      onClick={openAddHandleClick}
                    />
                  </div>
                </div>

                <div className="loan-tabPanel">
                  <TabPanel>
                    {/* CATEGORIES TABLE */}
                    <CreateTable
                      tableName={"Categories"}
                      headers={headersCategory}
                      accessors={accessorsCategory}
                      data={displayData}
                      actions={actions}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      itemsPerPage={10}
                      totalItems={displayData?.length}
                    />
                  </TabPanel>
                  <TabPanel>
                    {/* REQUIREMENTS TABLE */}
                    <CreateTable
                      tableName={"Requirements"}
                      headers={headersRequirement}
                      accessors={accessorsRequirement}
                      data={displayData}
                      actions={actions}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      itemsPerPage={10}
                      totalItems={displayData?.length}
                    />
                  </TabPanel>
                </div>
              </Tabs>
            </div>
          </div>
        </div>
      </div>

      <AddEditForm
        isOpen={showAddForm}
        onClose={closeAddHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleSubmit}
        modalName={activeTab === 0 ? "Add Category" : "Add Requirement"}
        activeTab={activeTab}
        categories={categories}
        mode="add"
      />
      <AddEditForm
        isOpen={showEditForm}
        onClose={closeEditHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleUpdate}
        modalName={activeTab === 0 ? "Update Category" : "Update Requirement"}
        activeTab={activeTab}
        categories={categories}
        mode="edit"
      />

      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />

      <LoadingSpinner loading={loadingRequest || loading} />

    </div>
  )
}

function AddEditForm({
  isOpen,
  modalName,
  onClose,
  form,
  setForm,
  handleSubmit,
  activeTab,
  categories,
  mode = "add",
}) {
  const [errorState, setErrorState] = useState(false);

  const submit = (e) => {
    e.preventDefault();

    // Validation
    let isValid = true;
    if (!form.description) isValid = false;

    // Special validation for Requirement tab
    if (activeTab === 1 && !form.categoryId) isValid = false;

    if (!isValid) {
      setErrorState(true);
      return;
    }

    handleSubmit(e);
    setErrorState(false);
  };

  const handleClose = () => {
    onClose();
    setErrorState(false);
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  const isEditMode = mode === "edit";

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto"
      >
        <div className="bg-gray-50 w-full md:w-[90%] lg:w-[50%] xl:w-[40%] rounded-lg shadow-xl h-auto max-h-[90vh] flex flex-col relative">

          {/* Header */}
          <div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
              <span className="text-xl text-gray-600">{modalName}</span>
            </h2>

            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none"
              aria-label="Close modal"
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto flex-grow space-y-4">

            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
              <div className="py-1 border-b border-gray-300">
                <h3 className="font-medium text-gray-700">Information</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 pb-3 pt-3">

                {/* Requirements Tab: Show Category Dropdown */}
                {activeTab === 1 && (
                  <div className="flex flex-col">
                    <label className="text-sm font-semibold text-gray-600 mb-1">
                      Category:
                    </label>
                    <select
                      className={`border rounded p-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errorState && !form.categoryId ? 'border-red-500' : 'border-gray-300'}`}
                      value={form.categoryId}
                      onChange={(e) => handleInputChange('categoryId', e.target.value)}
                    >
                      <option value="" disabled>Select a Category</option>
                      {categories.filter(c => c.isActive).map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.description}
                        </option>
                      ))}
                    </select>
                    {errorState && !form.categoryId && (
                      <span className="text-red-500 text-xs mt-1">Please select a category</span>
                    )}
                  </div>
                )}

                {/* Description Input (Used for both Category Name and Requirement Description) */}
                <Input
                  label={activeTab === 0 ? "Category Name:" : "Description:"}
                  value={form.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder={activeTab === 0 ? "Enter category name" : "Enter requirement description"}
                  isRequired={true}
                  error={errorState && !form.description}
                  errorMessage="Please fill up this field"
                />

                <div className="flex items-center pt-2">
                  <input
                    type="checkbox"
                    checked={form.isActive !== undefined ? form.isActive : true}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label className="ml-2 text-sm font-medium text-gray-600">
                    Active
                  </label>
                </div>
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white rounded-b-lg shadow-sm">
            <button
              onClick={handleClose}
              className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors"
            >
              Cancel
            </button>

            <button
              onClick={submit}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors"
            >
              {isEditMode ? "Update" : "Save"}
            </button>
          </div>

        </div>
      </div>
    </>
  );
}

export default Requirements